/**
 * Pure helpers behind the Telemetry page: install status, version ordering,
 * and the version chart's rows. Kept apart from the components so they test
 * without rendering.
 */
import type { DeviceInstall, VersionCount } from '../../api/telemetry';

export type InstallStatus = 'Stranded' | 'Failing' | 'Outdated' | 'OK';

/** Worst first: leads stuck on a phone beat a failing sync beat an old build. */
export const STATUS_RANK: Record<InstallStatus, number> = { Stranded: 0, Failing: 1, Outdated: 2, OK: 3 };

export function installStatus(i: DeviceInstall): InstallStatus {
  if (i.stranded) return 'Stranded';
  if (i.consecutiveSyncFailures > 0) return 'Failing';
  if (i.outdated) return 'Outdated';
  return 'OK';
}

/** Numeric dotted-version compare (1.10.0 > 1.2.0); anything else sorts below. */
export function compareVersions(a: string | null, b: string | null): number {
  const parse = (v: string | null) => (v && /^\d+(\.\d+)*$/.test(v) ? v.split('.').map(Number) : null);
  const pa = parse(a);
  const pb = parse(b);
  if (!pa || !pb) return (pa ? 1 : 0) - (pb ? 1 : 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? -1) - (pb[i] ?? -1);
    if (d !== 0) return d;
  }
  return 0;
}

/** Versions shown as their own bars; the rest fold into one "Older versions" bar. */
const MAX_VERSION_ROWS = 8;

export interface VersionRow {
  label: string;
  ios: number;
  android: number;
  other: number;
  total: number;
}

export const PLATFORM_KEYS = ['ios', 'android', 'other'] as const;

/** One bar per version (newest first, as the API orders them), platforms stacked; the tail folds into "Older versions". */
export function toVersionRows(versions: VersionCount[], latestVersion: string | null): VersionRow[] {
  const rows: VersionRow[] = [];
  const byLabel = new Map<string, VersionRow>();
  for (const v of versions) {
    let label = v.appVersion ?? 'Not reported';
    if (v.appVersion && v.appVersion === latestVersion) label = `${v.appVersion} (latest)`;
    let row = byLabel.get(label);
    if (!row) {
      row = { label, ios: 0, android: 0, other: 0, total: 0 };
      byLabel.set(label, row);
      rows.push(row);
    }
    const key = v.platform === 'ios' ? 'ios' : v.platform === 'android' ? 'android' : 'other';
    row[key] += v.installs;
    row.total += v.installs;
  }
  if (rows.length <= MAX_VERSION_ROWS) return rows;
  const kept = rows.slice(0, MAX_VERSION_ROWS - 1);
  const rest = rows.slice(MAX_VERSION_ROWS - 1).reduce<VersionRow>(
    (acc, r) => ({
      ...acc,
      ios: acc.ios + r.ios,
      android: acc.android + r.android,
      other: acc.other + r.other,
      total: acc.total + r.total,
    }),
    { label: 'Older versions', ios: 0, android: 0, other: 0, total: 0 },
  );
  return [...kept, rest];
}
